import React, { useState } from "react";
import { useQuote } from "../context/QuoteContext";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Package, Plus, Minus, Trash2 } from "lucide-react";
import { formatEur } from "../utils";

export default function PackingSection() {
  const {
    packingEnabled, setPackingEnabled, packingCatalog, packingItems,
    addPackingItem, updatePackingItem, removePackingItem, setPackingClientTotal,
    packingCostTotal, packingClientTotal, packingMargin,
  } = useQuote();
  const [sel, setSel] = useState("");

  const onSelect = (label) => {
    const m = (packingCatalog || []).find((c) => c.label === label);
    if (m) addPackingItem(m);
    setSel("");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="packing-section">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5 text-[#1a5f5a]" />
          <div>
            <h3 className="font-display font-semibold text-lg">Materiali d'imballaggio richiesti</h3>
            <p className="text-xs text-slate-400">Uso interno: incide solo sul Profit-o-metro (margine spalmato per periodo), non sul prezzo mostrato al cliente.</p>
          </div>
        </div>
        <Switch checked={!!packingEnabled} onCheckedChange={setPackingEnabled} data-testid="packing-toggle" />
      </div>

      {packingEnabled && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-4">
          <div className="flex items-center gap-2">
            <Select value={sel} onValueChange={onSelect}>
              <SelectTrigger className="flex-1" data-testid="packing-material-select"><SelectValue placeholder="Aggiungi materiale dal catalogo…" /></SelectTrigger>
              <SelectContent>
                {(packingCatalog || []).map((c) => (
                  <SelectItem key={c.label} value={c.label} data-testid={`packing-cat-${c.label}`}>
                    {c.label} — costo €{formatEur(c.cost_eur)} · cliente €{formatEur(c.client_eur)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {packingItems.length === 0 ? (
            <p className="text-slate-400 text-sm py-2 text-center border border-dashed border-slate-200 rounded-md">Nessun materiale aggiunto.</p>
          ) : (
            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-[1fr_auto_90px_90px_70px_32px] gap-2 text-[10px] uppercase tracking-wide text-slate-400 px-1">
                <span>Materiale</span><span className="text-center">Q.tà</span><span>Costo/unità</span><span>Cliente/unità</span><span className="text-right">Subtot.</span><span></span>
              </div>
              {packingItems.map((i) => (
                <div key={i.id} className="grid grid-cols-[1fr_auto_90px_90px_70px_32px] gap-2 items-center" data-testid={`packing-item-${i.id}`}>
                  <span className="text-sm text-slate-700 truncate">{i.label}</span>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updatePackingItem(i.id, { qty: Math.max(1, i.qty - 1) })}><Minus className="h-3 w-3" /></Button>
                    <span className="w-6 text-center text-sm font-medium" data-testid={`packing-qty-${i.id}`}>{i.qty}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updatePackingItem(i.id, { qty: i.qty + 1 })}><Plus className="h-3 w-3" /></Button>
                  </div>
                  <Input type="number" value={i.cost} onChange={(e) => updatePackingItem(i.id, { cost: parseFloat(e.target.value) || 0 })} className="h-8" data-testid={`packing-item-cost-${i.id}`} />
                  <Input type="number" value={i.client} onChange={(e) => updatePackingItem(i.id, { client: parseFloat(e.target.value) || 0 })} className="h-8" data-testid={`packing-item-client-${i.id}`} />
                  <span className="text-right text-sm text-slate-500 tabular-nums">€{formatEur((Number(i.client) || 0) * (Number(i.qty) || 0))}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => removePackingItem(i.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}

              <div className="border-t border-slate-100 pt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="bg-slate-50 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Totale nostro (costo)</div>
                  <div className="font-display font-semibold text-slate-700" data-testid="packing-cost-total">€{formatEur(packingCostTotal)}</div>
                </div>
                <div className="bg-slate-50 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Totale cliente</div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-500">€</span>
                    <Input type="number" value={Math.round(packingClientTotal * 100) / 100}
                      onChange={(e) => setPackingClientTotal(parseFloat(e.target.value) || 0)}
                      className="h-8 font-semibold" data-testid="packing-client-total-input" />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">Modificandolo, i prezzi cliente si ridistribuiscono proporzionalmente.</div>
                </div>
                <div className={`rounded-md p-3 border ${packingMargin < 0 ? "bg-red-50 border-red-100" : "bg-emerald-50 border-emerald-100"}`}>
                  <div className={`text-[10px] uppercase tracking-wide ${packingMargin < 0 ? "text-red-600" : "text-emerald-600"}`}>Margine (nel Profit-o-metro)</div>
                  <div className={`font-display font-semibold ${packingMargin < 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="packing-margin">{packingMargin < 0 ? "-" : ""}€{formatEur(Math.abs(packingMargin))}</div>
                  <div className="text-[10px] text-slate-400 mt-1">Cliente − costo, spalmato per periodo. Se il costo supera il prezzo cliente, riduce la redditività.</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
